import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useQuery} from "@tanstack/react-query";
import vmsService from "../../../../services/web/vms-service.js";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import VmsModal from "../../video-cameras/components/vms-modal.jsx";
import {Template} from "../../../../components/template/template.jsx";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import SelectComponent from "../../../../components/select/select-component.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilter = {
    name: "",
    vms: ""
};
const filterGlobal = {...initialFilter};

const RecordersList = () => {
    const [recorders, setRecorders] = useState([]);
    const [filteredRecorders, setFilteredRecorders] = useState([]);
    const [filter, setFilter] = useState(filterGlobal);
    const [vmsModal, setVmsModal] = useState({open: false, target: "recorder"});

    const gridRef = useRef();
    const gridApi = useRef(null);

    const {data, isSuccess, isLoading: isLoadingRecorders} = useQuery({
        queryFn: () => vmsService.recordersList(),
        queryKey: ["vms-recorders"]
    })

    const {data: vmsData, isLoading: isLoadingVmsData} = useQuery({
        queryFn: () => vmsService.getAll(),
        queryKey: ["vms-recorders-all"]
    });

    useEffect(() => {
        if (!isSuccess) return;

        setRecorders(data);
        setFilteredRecorders(data);
    }, [isSuccess, data]);

    const openVms = (target) => {
        if (vmsData.length > 1) {
            setVmsModal({open: true, target})
            return;
        }


        const vmsTab = window.open("", "_blank");
        vmsTab.opener = null;
        axios.get(`${API_URL}/vms/access-from-collection-system/${vmsData[0].id}`)
            .then(({data}) => vmsTab.location.href = data)
            .catch(() => {
                vmsTab.close();
                projectUtils.swalErrorMessage("", "Não foi possível abrir o VMS.", "Ok");
            })
    }

    const columnDefs = [
        { headerName: "ID", field: "id", width: 100, filter: true },
        { headerName: "Nome", field: "name", flex: 1, filter: true, sort: "asc",
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
        { headerName: "VMS", field: "vmsTitle", width: 220, filter: true },
        { headerName: "Qtd. câmeras", field: "camerasCount", width: 150,
            comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
                const a = valueA ?? 0;
                const b = valueB ?? 0;

                if (a === 0 && b === 0) return 0;

                if (a === 0) return isInverted ? -1 : 1;
                if (b === 0) return isInverted ? 1 : -1;

                return a - b;
            }
        }
    ];

    const defaultColDef = useMemo(() => {
        return {
            filter: false,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const onCellClicked = async ({data}) => {
        const vmsTab = window.open("", "_blank");
        vmsTab.opener = null;
        axios.get(`${API_URL}/vms/access-from-collection-system/${data.vmsId}/${data.id}`)
            .then(({data}) => vmsTab.location.href = data)
            .catch(() => {
                vmsTab.close();
                projectUtils.swalErrorMessage("", "Não foi possível abrir o VMS.", "Ok");
            })
    }

    const handleInputs = ({ target: { id, value } }) =>
        setFilter(prev => ({ ...prev, [id]: value }));

    const normalize = (value) =>
        value
            ?.toString()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

    const apply = useCallback(() => {
        let result = [...recorders];

        if (filter.name) {
            const name = normalize(filter.name);

            result = result.filter(item =>
                normalize(item.name)?.includes(name)
            );
        }

        if (filter.vms) {
            result = result.filter(item =>
                item.vmsId === filter.vms
            );
        }

        setFilteredRecorders(result);
    }, [filter, recorders]);

    const clear = () => {
        setFilter(initialFilter);
        setFilteredRecorders(recorders);
    };

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    return (
        <>
            <Template isLoading={isLoadingRecorders || isLoadingVmsData}>
                <Template.Header>
                    <Template.Title>
                        Gravadores
                    </Template.Title>

                    { vmsData?.length > 0 &&
                        <Button target={"primary"} onClick={() => openVms("recorder")}>
                            <FaPlus/>
                            Adicionar gravador
                        </Button>
                    }
                </Template.Header>

                <Template.Body>
                    <Card>
                        <Card.Body>
                            <SentryRow>
                                <InputForm
                                    id="name"
                                    value={filter.name}
                                    onChange={handleInputs}
                                    label="Pesquisar"
                                    classNameWrapper={"col-span-8"}
                                />

                                <div className="flex justify-between col-span-4 gap-4">
                                    <SelectComponent
                                        id="vms"
                                        label="VMS"
                                        value={filter.vms}
                                        options={(vmsData || []).map((e) => ({ value: e.id, label: e.vmsCorporationName || e.vmsUrl }))}
                                        onChange={(option) => setFilter(prev => ({...prev, vms: option?.value}))}
                                        classNameWrapper={"flex-1"}
                                    />

                                    <div className={"flex gap-2"}>
                                        <Button title="Limpar" onClick={clear} target={"primary-outline"}>
                                            <FaEraser />
                                        </Button>
                                        <Button title="Buscar" onClick={apply}>
                                            <FaSearch />
                                        </Button>
                                    </div>
                                </div>
                            </SentryRow>
                        </Card.Body>
                    </Card>

                    <Card className={"mt-2"}>
                        <Card.Body>
                            <div className={`aggrid-row-pointer`} style={{height: 'calc(100vh - 153px)'}}>
                                <AgGridReact
                                    onGridReady={(params) => {
                                        gridApi.current = params.api;
                                    }}
                                    ref={gridRef}
                                    rowData={filteredRecorders}
                                    columnDefs={columnDefs}
                                    onCellClicked={onCellClicked}
                                    suppressHeaderFocus={true}
                                    defaultColDef={defaultColDef}
                                    suppressRowVirtualisation={true}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    theme={aggridUtils.generalTheme()}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <VmsModal vmsModal={vmsModal} data={vmsData} setVmsModal={setVmsModal}/>
        </>
    );
};

export default RecordersList;
